import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Truck, UserX } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import ConfirmModal from '../shared/ConfirmModal';
import { suppliersAPI } from '../../services/warehouseAPI';
import { Supplier } from '../../types/warehouse';
import { LoadFailed } from '../settings/instantSettings';
import { SheetField } from '../settings/sheet';
import {
  Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, ListToolbar, PRIMARY_BTN, StatusChip,
  matchesSearch,
} from '../shared/listPage';

/**
 * Αποθήκη → «Προμηθευτές» (14/10/2026): λίστα με αναζήτηση + συρτάρι με τη φόρμα. Ίδια κλήσεις με πριν
 * (services/warehouseAPI: suppliersAPI.getAll/create/update/delete) και ίδια επικύρωση του browser (όνομα
 * υποχρεωτικό, email σε μορφή email). Η «Απενεργοποίηση» (DELETE — ο server κρατά τον προμηθευτή ανενεργό)
 * πήγε από την κάρτα στο κάτω μέρος του συρταριού, με την ίδια ερώτηση.
 */

const emptyForm = (): Partial<Supplier> => ({
  name: '',
  code: '',
  contact_person: '',
  phone: '',
  email: '',
  address: '',
  tax_number: '',
  notes: ''
});

const SuppliersManagement: React.FC = () => {
  const { t } = useTranslation(['warehouse', 'common', 'listPage']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const uid = useId();
  const formId = `${uid}-form`;
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [formData, setFormData] = useState<Partial<Supplier>>(emptyForm);

  useEffect(() => {
    if (store?.id) {
      fetchSuppliers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const data = await suppliersAPI.getAll(store!.id);
      setSuppliers(data || []);
      setLoadFailed(false);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSupplier(null);
    setFormData(emptyForm());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editingSupplier) {
        await suppliersAPI.update(store!.id, editingSupplier.id, formData);
      } else {
        await suppliersAPI.create(store!.id, formData);
      }
      fetchSuppliers();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save supplier:', error);
      showToast('error', t('warehouse:failedSaveSupplier'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (supplierId: number) => {
    setConfirmModal({
      open: true,
      title: t('warehouse:deactivateSupplier'),
      message: t('warehouse:confirmDeactivateSupplier'),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        try {
          await suppliersAPI.delete(store!.id, supplierId);
          showToast('success', t('warehouse:supplierDeactivated'));
          // Ο προμηθευτής απενεργοποιήθηκε: το συρτάρι του κλείνει.
          handleCloseModal();
          fetchSuppliers();
        } catch (error) {
          console.error('Failed to delete supplier:', error);
          showToast('error', t('warehouse:cannotDeleteSupplier'));
        }
      }
    });
  };

  const handleCreate = () => {
    setEditingSupplier(null);
    setFormData(emptyForm());
    setShowModal(true);
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      code: supplier.code || '',
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      tax_number: supplier.tax_number || '',
      notes: supplier.notes || ''
    });
    setShowModal(true);
  };

  // Esc / X / φόντο: πρώτα το παράθυρο επιβεβαίωσης, αν είναι ανοιχτό.
  const requestClose = () => {
    if (confirmModal.open) {
      setConfirmModal(prev => ({ ...prev, open: false }));
      return;
    }
    if (saving) return;
    handleCloseModal();
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    matchesSearch(searchTerm, supplier.name, supplier.code, supplier.contact_person)
  );

  const set = (key: keyof Supplier) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData(prev => ({ ...prev, [key]: e.target.value }));

  const columns: Column<Supplier>[] = [
    {
      key: 'name',
      header: t('warehouse:ui.suppliers.name'),
      width: 'minmax(180px, 2fr)',
      stack: 'title',
      render: s => (
        <>
          <span className="block font-medium">{s.name}</span>
          {(s.code || s.address) && (
            <span className="block text-xs text-t3 truncate">
              {[s.code && t('warehouse:ui.suppliers.codeValue', { code: s.code }), s.address].filter(Boolean).join(' · ')}
            </span>
          )}
        </>
      ),
      stackRender: s => s.name,
    },
    {
      key: 'contact_person',
      header: t('warehouse:ui.suppliers.contact'),
      width: 'minmax(140px, 1.2fr)',
      render: s => (s.contact_person ? <span className="text-t2">{s.contact_person}</span> : <span className="text-t4">—</span>),
      stackRender: s => s.contact_person || null,
    },
    {
      key: 'phone',
      header: t('common:phone'),
      width: 'minmax(130px, 1fr)',
      render: s => (s.phone ? <span className="tabular-nums">{s.phone}</span> : <span className="text-t4">—</span>),
      stackRender: s => s.phone || null,
    },
    {
      key: 'email',
      header: t('common:email'),
      width: 'minmax(160px, 1.3fr)',
      hideBelow: 'medium',
      render: s => (s.email ? <span className="block truncate text-t2">{s.email}</span> : <span className="text-t4">—</span>),
    },
    {
      key: 'counts',
      header: t('warehouse:ui.suppliers.linked'),
      width: 'minmax(140px, 1fr)',
      hideBelow: 'medium',
      render: s => {
        const parts = [
          s.raw_materials_count !== undefined ? t('warehouse:ui.suppliers.materialsCount', { count: Number(s.raw_materials_count) }) : null,
          s.products_count !== undefined ? t('warehouse:ui.suppliers.productsCount', { count: Number(s.products_count) }) : null,
        ].filter(Boolean);
        return parts.length ? <span className="text-t2">{parts.join(' · ')}</span> : <span className="text-t4">—</span>;
      },
    },
    {
      key: 'is_active',
      header: t('warehouse:ui.suppliers.status'),
      width: '110px',
      stack: 'end',
      render: s => (
        <StatusChip tone={s.is_active ? 'ok' : 'muted'}>
          {s.is_active ? t('warehouse:activeStatus') : t('warehouse:inactiveStatus')}
        </StatusChip>
      ),
    },
  ];

  const fieldId = (name: string) => `${uid}-${name}`;

  return (
    <>
      <ListToolbar
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: t('warehouse:searchSuppliers'),
          ariaLabel: t('warehouse:ui.suppliers.searchLabel'),
        }}
        actions={(
          <button type="button" className={PRIMARY_BTN} onClick={handleCreate}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('warehouse:ui.suppliers.add')}
          </button>
        )}
        summary={!loading && !loadFailed ? t('warehouse:ui.suppliers.count', { count: filteredSuppliers.length }) : null}
      />

      {loadFailed ? (
        <LoadFailed message={t('warehouse:ui.suppliers.loadFailed')} onRetry={fetchSuppliers} />
      ) : (
        <DataTable
          columns={columns}
          rows={filteredSuppliers}
          rowKey={s => s.id}
          onRowClick={handleEdit}
          selectedKey={showModal ? editingSupplier?.id ?? null : null}
          loading={loading}
          stickyHeader
          ariaLabel={t('warehouse:ui.tabs.suppliers')}
          rowLabel={s => t('warehouse:ui.suppliers.rowLabel', { name: s.name })}
          testId="suppliers-table"
          empty={searchTerm.trim() ? (
            <EmptyState title={t('warehouse:ui.suppliers.noResults')} hint={t('warehouse:ui.tryAnotherSearch')} />
          ) : (
            <EmptyState icon={Truck} title={t('warehouse:ui.suppliers.empty')} hint={t('warehouse:ui.suppliers.emptyHint')} />
          )}
        />
      )}

      {/* Add/Edit — συρτάρι */}
      <DetailPanel
        open={showModal}
        onClose={requestClose}
        title={editingSupplier ? t('warehouse:ui.suppliers.editTitle') : t('warehouse:ui.suppliers.newTitle')}
        subtitle={editingSupplier?.name}
        footerStart={editingSupplier ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => handleDelete(editingSupplier.id)} disabled={saving}>
            <UserX className="w-4 h-4" aria-hidden="true" />
            {t('warehouse:ui.suppliers.deactivate')}
          </button>
        ) : undefined}
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={requestClose} disabled={saving}>{t('warehouse:cancelShort')}</button>
            <button type="submit" form={formId} className={PRIMARY_BTN} disabled={saving} aria-busy={saving || undefined}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {editingSupplier ? t('warehouse:ui.save') : t('warehouse:ui.create')}
            </button>
          </>
        )}
        testId="supplier-panel"
      >
        {showModal && (
          // Χωρίς noValidate: ο browser ελέγχει το «Όνομα» (required) και τη μορφή του email, όπως πριν.
          <form id={formId} onSubmit={handleSubmit} className="space-y-4" data-testid="supplier-form">
            <div className="xs-grid2">
              <SheetField label={t('warehouse:ui.fields.nameRequired')} htmlFor={fieldId('name')}>
                <input id={fieldId('name')} type="text" value={formData.name} onChange={set('name')} required autoComplete="off" className={INPUT_CLS} />
              </SheetField>
              <SheetField label={t('warehouse:ui.fields.code')} htmlFor={fieldId('code')}>
                <input id={fieldId('code')} type="text" value={formData.code} onChange={set('code')} autoComplete="off" className={INPUT_CLS} />
              </SheetField>
            </div>
            <div className="xs-grid2">
              <SheetField label={t('warehouse:ui.fields.contactPerson')} htmlFor={fieldId('contact')}>
                <input id={fieldId('contact')} type="text" value={formData.contact_person} onChange={set('contact_person')} autoComplete="off" className={INPUT_CLS} />
              </SheetField>
              <SheetField label={t('common:phone')} htmlFor={fieldId('phone')}>
                <input id={fieldId('phone')} type="text" inputMode="tel" value={formData.phone} onChange={set('phone')} autoComplete="off" className={`${INPUT_CLS} tabular-nums`} />
              </SheetField>
            </div>
            <div className="xs-grid2">
              <SheetField label={t('common:email')} htmlFor={fieldId('email')}>
                <input id={fieldId('email')} type="email" value={formData.email} onChange={set('email')} autoComplete="one-time-code" className={INPUT_CLS} />
              </SheetField>
              <SheetField label={t('warehouse:taxNumber')} htmlFor={fieldId('tax')}>
                <input id={fieldId('tax')} type="text" value={formData.tax_number} onChange={set('tax_number')} autoComplete="off" className={`${INPUT_CLS} tabular-nums`} />
              </SheetField>
            </div>
            <SheetField label={t('common:address')} htmlFor={fieldId('address')}>
              <input id={fieldId('address')} type="text" value={formData.address} onChange={set('address')} autoComplete="off" className={INPUT_CLS} />
            </SheetField>
            <SheetField label={t('common:notes')} htmlFor={fieldId('notes')}>
              <textarea id={fieldId('notes')} rows={3} value={formData.notes} onChange={set('notes')} className={`${INPUT_CLS} resize-y`} />
            </SheetField>
          </form>
        )}
        <ConfirmModal
          isOpen={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        />
      </DetailPanel>
    </>
  );
};

export default SuppliersManagement;
